import React from 'react';
import { Box, Typography, TextField, Tabs, Tab, Stack, Chip, Divider, Checkbox, FormControlLabel, Button } from '@mui/material';
import roleService, { RoleDto } from '../../services/roleService';

interface RoleDetailsProps {
  roleId: string | null;
}

const ModuleRow: React.FC<{ label: string }> = ({ label }) => {
  const [state, setState] = React.useState({ read: false, create: false, update: false, remove: false, export: false });
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
      <Box sx={{ width: 180, fontWeight: 600 }}>{label}</Box>
      <FormControlLabel control={<Checkbox checked={state.read} onChange={(e) => setState(s => ({ ...s, read: e.target.checked }))} />} label="Podgląd" />
      <FormControlLabel control={<Checkbox checked={state.create} onChange={(e) => setState(s => ({ ...s, create: e.target.checked }))} />} label="Tworzenie" />
      <FormControlLabel control={<Checkbox checked={state.update} onChange={(e) => setState(s => ({ ...s, update: e.target.checked }))} />} label="Edycja" />
      <FormControlLabel control={<Checkbox checked={state.remove} onChange={(e) => setState(s => ({ ...s, remove: e.target.checked }))} />} label="Usuwanie" />
      <FormControlLabel control={<Checkbox checked={state.export} onChange={(e) => setState(s => ({ ...s, export: e.target.checked }))} />} label="Eksport" />
    </Stack>
  );
};

const MODULES: Array<{ key: keyof RoleDto['modules']; label: string }> = [
  { key: 'home', label: 'Strona główna' },
  { key: 'projects', label: 'Projekty' },
  { key: 'offers', label: 'Ofertowanie' },
  { key: 'database', label: 'Bazy danych' },
  { key: 'invoices', label: 'Faktury' },
  { key: 'analytics', label: 'Analityka' },
  { key: 'service', label: 'Serwis' },
  { key: 'permissions', label: 'Uprawnienia' },
];

// Aplikacje dostępne pod ścieżką /app – nadajemy uprawnienia indywidualnie
const APPS: Array<{ key: keyof RoleDto['modules']; label: string }> = [
  { key: 'estimates', label: 'Kosztorysy' },
  { key: 'materials_approvals', label: 'Karty materiałowe' },
  { key: 'schedules', label: 'Harmonogram' },
  { key: 'maps', label: 'Logistyka / Mapy' },
  { key: 'human_resources', label: 'HR' },
  { key: 'requester', label: 'Requester' },
  { key: 'tasker', label: 'Tasker' },
  { key: 'contracts', label: 'Umowy' },
];

const RoleDetails: React.FC<RoleDetailsProps> = ({ roleId }) => {
  const [role, setRole] = React.useState<RoleDto | null>(null);
  const [name, setName] = React.useState('');
  const [desc, setDesc] = React.useState('');
  const [mods, setMods] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    const load = async () => {
      if (!roleId) { setRole(null); return; }
      const all = await roleService.listRoles();
      const found = all.find(r => String(r.id) === roleId);
      if (found) {
        setRole(found); setName(found.name); setDesc(found.description || '');
        // Ustaw domyślne wartości aplikacji: admin → włączone, inne → wyłączone, tylko gdy klucz nie istnieje
        const base = { ...(found.modules || {}) } as Record<string, boolean>;
        const isAdminRole = String(found.name || '').toLowerCase() === 'admin';
        APPS.forEach(a => {
          if (base[a.key as string] === undefined) {
            base[a.key as string] = isAdminRole ? true : false;
          }
        });
        setMods(base);
        // ukrywamy edycję list – robi to backend
      }
    };
    load();
  }, [roleId]);

  if (!roleId || !role) return <Typography variant="body2" color="text.secondary">Wybierz rolę z listy po lewej lub utwórz nową.</Typography>;

  const save = async () => {
    const updated = await roleService.updateRole(role.id, { name, description: desc, modules: mods });
    setRole(updated);
    // odśwież listę ról po lewej
    window.dispatchEvent(new Event('roles:refresh'));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Szczegóły roli: <Chip size="small" label={role.name} sx={{ ml: 1 }} /></Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField label="Nazwa roli" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Opis" fullWidth multiline minRows={2} value={desc} onChange={(e) => setDesc(e.target.value)} />
      </Stack>
      <Divider sx={{ my: 2 }} />
      {/* Dostęp do projektów/ofert ustawiany jest z poziomu karty Użytkownicy → Modyfikuj uprawnienia */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Uprawnienia modułów</Typography>
      <Box>
        {MODULES.map(m => (
          <FormControlLabel key={m.key as string} control={<Checkbox checked={!!mods[m.key as string]} onChange={(e) => setMods(prev => ({ ...prev, [m.key as string]: e.target.checked }))} />} label={m.label} />
        ))}
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Aplikacje</Typography>
      <Box>
        {APPS.map(m => (
          <FormControlLabel key={m.key as string} control={<Checkbox checked={!!mods[m.key as string]} onChange={(e) => setMods(prev => ({ ...prev, [m.key as string]: e.target.checked }))} />} label={m.label} />
        ))}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={save}>Zapisz rolę</Button>
      </Box>
    </Box>
  );
};

export default RoleDetails;


